import { formatCountryList } from '@wit-support/ui-domain';
import { ADS_TZ, formatMinor, formatMomentInZone } from '../format.js';
import { STRUCTURED_TYPES, adsTypeOf, humanText, parseJsonish, structuredOrHuman, } from './structured-display.js';
/** Сколько названий стран показать в строке; остальные — «и ещё K» (весь список — `displayTitle`). */
export const COUNTRY_NAMES_SHOWN = 5;
/** «DE, FR» / «DE+FR» / «de fr» — показ, который целиком состоит из ISO-кодов. */
const CODES_ONLY = /^[A-Za-z]{2}(\s*[,+;\s]\s*[A-Za-z]{2})*$/;
function countriesText(codes) {
    return formatCountryList(codes, COUNTRY_NAMES_SHOWN).text;
}
const START_LABEL = {
    now: 'Сразу',
    midnight: 'По нулям (Лос-Анджелес)',
    account_hour: 'Час кабинета (Лос-Анджелес)',
};
function sameIds(a, b) {
    return a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');
}
/** Момент «В дату» — по Лос-Анджелесу, как его вводили: «15.10 03:00 Лос-Анджелес». */
function fmtIso(iso) {
    if (Number.isNaN(Date.parse(iso)))
        return iso;
    return formatMomentInZone(iso, ADS_TZ);
}
/**
 * Значение поля реестра словами — для чипов «Из шаблона», сводок и блока
 * «Зафиксировано системой». Структурные значения (квота, пол, плейсменты, …)
 * — своими словами; новый тип без правила — читаемым текстом (`humanText`),
 * никогда не JSON.
 */
export function displayValue(desc, value, ctx = {}) {
    const type = adsTypeOf(desc);
    // у структурных полей и «пусто» что-то значит: пол не задан — «Все»
    if (STRUCTURED_TYPES.has(type))
        return structuredOrHuman(desc, value);
    if (value === undefined || value === null) {
        if (desc.type === 'country_list')
            return 'не исключать';
        return '—';
    }
    switch (type) {
        case 'enum': {
            const opt = (desc.options ?? []).find((o) => String(o.value) === String(value));
            return opt?.label ?? String(value);
        }
        case 'money':
            return typeof value === 'number' ? formatMinor(value, ctx.currency ?? 'USD') : String(value);
        case 'int':
        case 'decimal':
            return String(value).replace('.', ',');
        case 'bool':
            return value === true ? 'вкл.' : 'выкл.';
        case 'int_range': {
            const r = value;
            return `${r.min ?? '?'}–${r.max ?? '?'}`;
        }
        case 'int_list':
            return Array.isArray(value) ? value.join(' · ') : String(value);
        case 'country_codes':
            return Array.isArray(value)
                ? value.length === 0
                    ? 'нет'
                    : countriesText(value.map(String))
                : String(value);
        case 'locales': {
            if (!Array.isArray(value))
                return String(value);
            const ids = value;
            const preset = ctx.localePresets?.find((p) => sameIds(p.localeIds, ids));
            if (preset)
                return preset.label;
            const names = ids.map((id) => ctx.locales?.find((l) => l.localeId === id)?.name ?? `#${id}`);
            return names.join(', ');
        }
        case 'audience_slots':
            // слоты — имена, которые задал человек: показываются как есть
            return Array.isArray(value)
                ? value.length === 0
                    ? 'нет'
                    : value.map((k) => String(k)).join(', ')
                : String(value);
        case 'interests':
            return Array.isArray(value)
                ? value.length === 0
                    ? 'нет'
                    : value.map((i) => i.name ?? String(i)).join(', ')
                : String(value);
        case 'country_list': {
            const list = ctx.countryLists?.find((l) => l.id === value);
            return list ? `${list.name} (${list.countries.length})` : String(value);
        }
        case 'geo': {
            const g = value;
            if (g.mode === 'WORLDWIDE')
                return 'Весь мир';
            if (g.mode === 'COUNTRIES')
                return (g.countries ?? []).length > 0
                    ? countriesText(g.countries ?? [])
                    : 'страны не выбраны';
            return humanText(value);
        }
        case 'start': {
            const s = value;
            if (s.mode === 'at' && s.at)
                return `В дату: ${fmtIso(s.at)}`;
            return START_LABEL[s.mode ?? ''] ?? humanText(value);
        }
        case 'text':
        case 'textarea':
        case 'url':
            return String(value);
        default:
            return humanText(value);
    }
}
/**
 * Подсказка к значению (для `title`): у стран — весь список названиями с
 * кодами, ведь в строке видны только первые `COUNTRY_NAMES_SHOWN`. У прочих
 * типов подсказки нет — значение уже показано целиком.
 */
export function displayTitle(desc, value, ctx = {}) {
    const full = (codes) => codes.length > 0 ? formatCountryList(codes, COUNTRY_NAMES_SHOWN).full : undefined;
    switch (desc.type) {
        case 'country_codes':
            return Array.isArray(value) ? full(value.map(String)) : undefined;
        case 'geo': {
            const g = value;
            return g?.mode === 'COUNTRIES' ? full(g.countries ?? []) : undefined;
        }
        case 'country_list': {
            const list = ctx.countryLists?.find((l) => l.id === value);
            return list ? full(list.countries) : undefined;
        }
        default:
            return undefined;
    }
}
/**
 * Значение поля `fixed`: структурное — словами из `fixedValue` (подпись
 * старого реестра бывала с кодами: «Все OPT_OUT», «lookalike=1»), иначе —
 * готовая подпись реестра, иначе — словами из `fixedValue`.
 */
export function displayFixed(desc, ctx = {}) {
    if (STRUCTURED_TYPES.has(adsTypeOf(desc)))
        return structuredOrHuman(desc, desc.fixedValue);
    if (desc.fixedDisplay != null && desc.fixedDisplay !== '')
        return desc.fixedDisplay;
    return displayValue(desc, desc.fixedValue, ctx);
}
/**
 * Готовый показ значения от auto-ads (сводка шаблона, таргетинг групп плана)
 * — словами, если сервер прислал код: id списка стран → «5 стран (5)»;
 * значение варианта (`home`, `frequently_in + home + recent`) → подпись
 * варианта реестра. Не узнали — показ сервера как есть.
 */
export function humanizeDisplay(display, desc, ctx = {}) {
    // показ «как есть» бывает JSON-ом (старый auto-ads, поле без `display`) — словами
    const json = parseJsonish(display);
    if (!desc)
        return json.ok ? humanText(json.value) : display;
    if (STRUCTURED_TYPES.has(adsTypeOf(desc))) {
        return json.ok ? structuredOrHuman(desc, json.value) : display;
    }
    if (json.ok)
        return displayValue(desc, json.value, ctx);
    if (desc.type === 'geo' || desc.type === 'country_codes') {
        const t = display.trim();
        if (t === 'WORLDWIDE')
            return 'Весь мир';
        // сервер прислал голые коды — назовём словами; любой другой показ — как есть
        if (CODES_ONLY.test(t))
            return countriesText(t.split(/[\s,+;]+/).map((c) => c.toUpperCase()));
        return display;
    }
    if (desc.type === 'country_list') {
        const list = ctx.countryLists?.find((l) => l.id === display.trim());
        return list ? displayValue(desc, list.id, ctx) : display;
    }
    const opts = desc.options ?? [];
    if (opts.length === 0)
        return display;
    const byValue = (v) => opts.find((o) => String(o.value) === v);
    const direct = byValue(display.trim());
    if (direct)
        return direct.label;
    // «frequently_in + home + recent» — значение `frequently_in,home,recent`, показанное через «+»
    const joined = byValue(display
        .split(/\s*[+,]\s*/)
        .filter((p) => p !== '')
        .join(','));
    return joined?.label ?? display;
}
//# sourceMappingURL=value-display.js.map